/**
 * @vuelto-surface
 * mobile: ok
 * desktop: n/a
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import type { BranchPermission, ModuleKey } from '../../../../shell/session/store/sessionTypes';
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { spacing } from '../../../../shared/theme/spacing'
import { typography } from '../../../../shared/theme/typography';
import type { EmployeeRecord } from '../../../store/useEmployeesStore';
import { permissionLevelFromTriple } from '../../../store/useEmployeesStore';
import { MODULE_LABELS_ES } from './permissionUiLabels';
import { permissionLevelShortLabel } from './permissionLevelOptions';

type Props = {
  employee: EmployeeRecord;
  visibleModules: ModuleKey[];
  branchFilter: string | null;
  onEdit: () => void;
};

export function EmployeeActiveCompactModules({
  employee,
  visibleModules,
  branchFilter,
  onEdit,
}: Props) {
  const rows = compactModuleRows(employee, visibleModules, branchFilter);

  return (
    <View style={styles.wrap} testID="perfil.team.permissions.employee-modules-compact">
      {rows.length === 0 ? (
        <Text style={styles.empty}>Sin módulos con acceso en esta sucursal.</Text>
      ) : (
        <View style={styles.grid}>
          {rows.map((row) => (
            <View key={row.module} style={styles.chip}>
              <Text style={styles.chipModule} numberOfLines={1}>
                {MODULE_LABELS_ES[row.module] ?? row.module}
              </Text>
              <Text style={styles.chipLevel}>{row.levelLabel}</Text>
            </View>
          ))}
        </View>
      )}
      <TouchableOpacity
        style={styles.editLink}
        onPress={onEdit}
        accessibilityRole="button"
        accessibilityLabel={`Editar permisos de ${employee.name}`}
      >
        <Text style={styles.editLinkText}>Editar permisos</Text>
      </TouchableOpacity>
    </View>
  );
}

function compactModuleRows(
  employee: EmployeeRecord,
  visibleModules: ModuleKey[],
  branchFilter: string | null
): Array<{ module: ModuleKey; levelLabel: string }> {
  const visible = new Set(visibleModules);
  const assignments = branchFilter
    ? employee.branchAssignments.filter((a) => a.branchId === branchFilter)
    : employee.branchAssignments;

  const bestByModule = new Map<ModuleKey, BranchPermission>();

  for (const assignment of assignments) {
    for (const perm of assignment.permissions) {
      if (!visible.has(perm.module) || !perm.canView) continue;
      const existing = bestByModule.get(perm.module);
      if (!existing || permRank(perm) > permRank(existing)) {
        bestByModule.set(perm.module, perm);
      }
    }
  }

  return [...bestByModule.entries()]
    .map(([module, perm]) => ({
      module,
      levelLabel: permissionLevelShortLabel(
        module,
        permissionLevelFromTriple({
          canView: perm.canView,
          canEdit: perm.canEdit,
          canVoid: perm.canVoid ?? false,
          canDiscount: perm.canDiscount ?? false,
          canManageAgenda: perm.canManageAgenda ?? false,
        })
      ),
    }))
    .sort((a, b) =>
      (MODULE_LABELS_ES[a.module] ?? a.module).localeCompare(
        MODULE_LABELS_ES[b.module] ?? b.module,
        'es'
      )
    );
}

function permRank(perm: BranchPermission): number {
  if (perm.canEdit && perm.canVoid && perm.canDiscount) return 4;
  if (perm.canEdit && perm.canVoid) return 3;
  if (perm.canEdit) return 2;
  if (perm.canView) return 1;
  return 0;
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    marginTop: spacing.sm,
  },
  empty: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    maxWidth: '100%',
  },
  chipModule: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
    flexShrink: 1,
  },
  chipLevel: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    fontSize: 11,
  },
  editLink: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
  },
  editLinkText: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
});
